import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    ArrowDownTrayIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    CreditCardIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";
import {
    buildBreakdownRows,
    buildCurrencyBreakdownRows,
    collectPaginatedRows,
    countBy,
    createExportFileName,
    exportRide2FiveExcelReport,
    sumBy,
} from "@/lib/export/excel";
import { getPayments } from "../api/walletPaymentsApi";
import type { PaymentsQueryParams, PaymentPurpose, PaymentStatus, PaymentMethod } from "../types";

// ============================================================================
// Constants
// ============================================================================

const purposeOptions: { value: PaymentPurpose | ""; label: string }[] = [
    { value: "", label: "All Purposes" },
    { value: "RIDE_BOOKING", label: "Ride Booking" },
    { value: "WALLET_TOP_UP", label: "Wallet Top Up" },
    { value: "SUBSCRIPTION", label: "Subscription" },
    { value: "OTHER", label: "Other" },
];

const statusOptions: { value: PaymentStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "PENDING", label: "Pending" },
    { value: "PROCESSING", label: "Processing" },
    { value: "SUCCESS", label: "Success" },
    { value: "FAILED", label: "Failed" },
    { value: "CANCELLED", label: "Cancelled" },
];

const methodOptions: { value: PaymentMethod | ""; label: string }[] = [
    { value: "", label: "All Methods" },
    { value: "CARD", label: "Card" },
    { value: "BANK", label: "Bank" },
    { value: "USSD", label: "USSD" },
    { value: "MOBILE_MONEY", label: "Mobile Money" },
    { value: "OTHER", label: "Other" },
];

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        PROCESSING: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        CANCELLED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s}
        </span>
    );
};

const PurposeBadge = ({ purpose }: { purpose: string }) => {
    const styles: Record<string, string> = {
        RIDE_BOOKING: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
        WALLET_TOP_UP: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
        SUBSCRIPTION: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
        OTHER: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[purpose] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {(purpose ?? "").replace(/_/g, " ")}
        </span>
    );
};

const MethodBadge = ({ method }: { method: string }) => (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
        <CreditCardIcon className="h-3 w-3" />
        {(method ?? "").replace(/_/g, " ")}
    </span>
);

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const PaymentsRoute = () => {
    const navigate = useNavigate();
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [filters, setFilters] = useState<PaymentsQueryParams>({
        page: 0,
        size: 20,
        purpose: "",
        status: "",
        method: "",
        search: "",
        dateFrom: "",
        dateTo: "",
    });
    const [showFilters, setShowFilters] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ["payments", filters],
        queryFn: () => getPayments(filters),
    });

    const payments = data?.data?.payments ?? [];
    const totalPages = data?.data?.totalPages ?? 0;
    const totalElements = data?.data?.totalElements ?? 0;
    const currentPage = filters.page ?? 0;

    const handleExport = async () => {
        try {
            setIsExporting(true);
            setExportError("");

            const paymentRows = await collectPaginatedRows({
                fetchPage: (page) =>
                    getPayments({
                        ...filters,
                        page,
                        size: 100,
                    }),
                getRows: (response) => response.data.payments,
                getTotalPages: (response) => response.data.totalPages,
            });

            const statusCounts = countBy(paymentRows, (payment) => payment.status);
            const purposeCounts = countBy(paymentRows, (payment) => payment.purpose);
            const methodCounts = countBy(paymentRows, (payment) => payment.method);
            const totalAmount = sumBy(paymentRows, (payment) => payment.amount);

            await exportRide2FiveExcelReport({
                title: "Payments Export",
                subtitle:
                    "Filtered payments export with payment volume, purpose, and method analysis.",
                fileName: createExportFileName("payments_export"),
                filters,
                rows: paymentRows,
                metrics: [
                    { label: "Filtered Payments", value: paymentRows.length },
                    {
                        label: "Total Payment Amount",
                        value: totalAmount.toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }),
                    },
                    {
                        label: "Successful Payments",
                        value: paymentRows.filter((payment) => payment.status === "SUCCESS").length,
                    },
                    {
                        label: "Failed Payments",
                        value: paymentRows.filter((payment) => payment.status === "FAILED").length,
                    },
                ],
                sections: [
                    {
                        title: "Status Breakdown",
                        rows: buildBreakdownRows(statusCounts),
                    },
                    {
                        title: "Purpose Breakdown",
                        rows: buildBreakdownRows(purposeCounts),
                    },
                    {
                        title: "Method Breakdown",
                        rows: buildBreakdownRows(methodCounts),
                    },
                    {
                        title: "Amount By Currency",
                        rows: buildCurrencyBreakdownRows(
                            paymentRows,
                            (payment) => payment.currency,
                            (payment) => payment.amount
                        ),
                    },
                ],
                columns: [
                    { header: "Payment ID", width: 38, value: (payment) => payment.paymentId },
                    { header: "User ID", width: 38, value: (payment) => payment.userId },
                    { header: "First Name", width: 18, value: (payment) => payment.firstName },
                    { header: "Last Name", width: 18, value: (payment) => payment.lastName },
                    { header: "Email", width: 28, value: (payment) => payment.email },
                    { header: "Purpose", width: 18, value: (payment) => payment.purpose },
                    { header: "Method", width: 16, value: (payment) => payment.method },
                    { header: "Status", width: 16, value: (payment) => payment.status },
                    {
                        header: "Amount",
                        width: 16,
                        alignment: "right",
                        value: (payment) => payment.amount,
                        numFmt: "#,##0.00",
                    },
                    { header: "Currency", width: 12, value: (payment) => payment.currency },
                    { header: "Reference", width: 24, value: (payment) => payment.reference || "—" },
                    {
                        header: "External Reference",
                        width: 24,
                        value: (payment) => payment.externalReference || "—",
                    },
                    {
                        header: "Payment Channel",
                        width: 18,
                        value: (payment) => payment.paymentChannel,
                    },
                    { header: "Paid At", width: 22, value: (payment) => (payment.paidAt ? formatDate(payment.paidAt) : "—") },
                    { header: "Created At", width: 22, value: (payment) => formatDate(payment.createdAt) },
                    {
                        header: "Failure Reason",
                        width: 28,
                        value: (payment) => payment.failureReason || "—",
                    },
                ],
                dataSheetName: "Payments",
            });
        } catch (error) {
            setExportError(extractApiError(error));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Toolbar */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search by reference…"
                        value={filters.search}
                        onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 0 }))}
                        className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-primary-500 dark:focus:ring-primary-900/30"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
                        <FunnelIcon className="mr-1.5 h-4 w-4" /> Filters
                    </Button>
                    <Button size="sm" onClick={handleExport} disabled={isExporting}>
                        <ArrowDownTrayIcon className="mr-1.5 h-4 w-4" />
                        {isExporting ? "Exporting..." : "Export Excel"}
                    </Button>
                </div>
            </div>

            {exportError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                    {exportError}
                </div>
            ) : null}

            {/* Filters */}
            {showFilters && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Purpose</label>
                            <select
                                value={filters.purpose}
                                onChange={(e) => setFilters((f) => ({ ...f, purpose: e.target.value as PaymentPurpose | "", page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            >
                                {purposeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as PaymentStatus | "", page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            >
                                {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Method</label>
                            <select
                                value={filters.method}
                                onChange={(e) => setFilters((f) => ({ ...f, method: e.target.value as PaymentMethod | "", page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            >
                                {methodOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Date From</label>
                            <input
                                type="date"
                                value={filters.dateFrom}
                                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value, page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Date To</label>
                            <input
                                type="date"
                                value={filters.dateTo}
                                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value, page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            />
                        </div>
                        <div className="flex items-end">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setFilters({ page: 0, size: 20, purpose: "", status: "", method: "", search: "", dateFrom: "", dateTo: "" })}
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
                    </div>
                ) : error ? (
                    <div className="py-20 text-center text-sm text-rose-500">{String(error)}</div>
                ) : payments.length === 0 ? (
                    <div className="py-20 text-center">
                        <CreditCardIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No payments found.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-800/50">
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">User</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Purpose</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Method</th>
                                    <th className="px-4 py-3 text-right font-medium text-slate-500 dark:text-slate-400">Amount</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Reference</th>
                                    <th className="px-4 py-3 text-center font-medium text-slate-500 dark:text-slate-400">Status</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {payments.map((payment) => (
                                    <tr
                                        key={payment.paymentId}
                                        onClick={() => navigate(`/wallet-payments/payments/${payment.paymentId}`)}
                                        className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                                                    <UserCircleIcon className="h-4 w-4 text-slate-400" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900 dark:text-white">
                                                        {payment.firstName} {payment.lastName}
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">{payment.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <PurposeBadge purpose={payment.purpose} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <MethodBadge method={payment.method} />
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-white">
                                            {formatCurrency(payment.amount, payment.currency)}
                                        </td>
                                        <td className="max-w-[140px] truncate px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                                            {payment.reference || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <StatusBadge status={payment.status} />
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                                            {formatDate(payment.paidAt || payment.createdAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 dark:border-slate-800">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Page {currentPage + 1} of {totalPages} · {totalElements} payments
                        </p>
                        <div className="flex gap-1">
                            <Button variant="secondary" size="sm" onClick={() => setFilters((f) => ({ ...f, page: currentPage - 1 }))} disabled={currentPage === 0}>
                                <ChevronLeftIcon className="h-4 w-4" />
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => setFilters((f) => ({ ...f, page: currentPage + 1 }))} disabled={currentPage >= totalPages - 1}>
                                <ChevronRightIcon className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
